'use client'

import { Suspense, lazy, useEffect, useMemo } from 'react'

import { useInView } from 'react-intersection-observer'
import { AdSlotErrorBoundary } from './AdSlotErrorBoundary'
import type { AdSize } from '@/helpers/getSizes.helper'
import { getSize } from '@/helpers/getSizes.helper'
import { loadGptScript } from '@/helpers/loadGptScript.helper'

const GoogleTagAd = lazy(() => import('./GoogleTagAd'))

export type AdSlotProps = {
  slot: string
  unit: string
  size?: AdSize
  className?: string
}

const AdSlot = ({ className = '', ...props }: AdSlotProps) => {
  const { inView, ref } = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })

  useEffect(() => {
    loadGptScript().catch(() => {
      // Ad script loading failures are non-critical — silently ignore
    })
  }, [])

  const dimensions = useMemo(() => getSize(props.size), [props.size])

  return (
    <div
      className={`ad-slot items-center justify-center rounded border border-gray-500 md:border-gray-300 ${className}`}
      style={{
        minWidth: dimensions.width,
        minHeight: dimensions.height,
      }}
      ref={ref}
    >
      {inView && (
        <AdSlotErrorBoundary>
          <Suspense>
            <GoogleTagAd {...props} className={className} />
          </Suspense>
        </AdSlotErrorBoundary>
      )}
    </div>
  )
}

export { AdSlot }
